const DB_NAME = 'chat-db'
const STORE_NAME = 'messages'
const DB_VERSION = 1
const EXPIRY_MS = 24 * 60 * 60 * 1000 // 1 day

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// 💾 Save chat messages
export async function saveMessages(messages) {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)

  const plainMessages = messages.map(m => ({
    type: m.type,
    text: m.text,
  }))

  store.put({
    id: 'chat',
    data: plainMessages,
    timestamp: Date.now(),
  })

  return tx.done || tx.complete
}

// 📥 Load chat messages with expiry check
export async function loadMessages() {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const store = tx.objectStore(STORE_NAME)

  return new Promise(resolve => {
    const req = store.get('chat')
    req.onsuccess = () => {
      const result = req.result
      if (!result || !Array.isArray(result.data)) return resolve(null)
      if (Date.now() - result.timestamp > EXPIRY_MS) return resolve(null)
      resolve(result.data)
    }
    req.onerror = () => resolve(null)
  })
}

// 🧵 Save assistant thread ID
export async function saveThreadId(threadId) {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)

  store.put({
    id: 'thread',
    data: threadId,
    timestamp: Date.now(),
  })

  return tx.done || tx.complete
}

// 📥 Load assistant thread ID with expiry
export async function loadThreadId() {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const store = tx.objectStore(STORE_NAME)

  return new Promise(resolve => {
    const req = store.get('thread')
    req.onsuccess = () => {
      const result = req.result
      if (!result || !result.data) return resolve(null)
      if (Date.now() - result.timestamp > EXPIRY_MS) return resolve(null)
      resolve(result.data)
    }
    req.onerror = () => resolve(null)
  })
}

// 🗑️ Clear saved messages
export async function clearMessages() {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)
  store.delete('chat')
  return tx.done || tx.complete
}

// 🗑️ Clear saved thread ID
export async function clearThreadId() {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)
  store.delete('thread')
  return tx.done || tx.complete
}

// 💾 Save resolved voiceId for a name (like Mila → UUID)
export async function saveVoiceId(voiceName, voiceId) {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)
  store.put({
    id: `voice-${voiceName.toLowerCase()}`,
    data: voiceId,
    timestamp: Date.now(),
  })
  return tx.done || tx.complete
}

// 📥 Load cached voiceId
export async function loadVoiceId(voiceName) {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const store = tx.objectStore(STORE_NAME)

  return new Promise(resolve => {
    const req = store.get(`voice-${voiceName.toLowerCase()}`)
    req.onsuccess = () => {
      const result = req.result
      if (!result || !result.data) return resolve(null)
      if (Date.now() - result.timestamp > 7 * 24 * 60 * 60 * 1000) return resolve(null) // 1 week
      resolve(result.data)
    }
    req.onerror = () => resolve(null)
  })
}

// 🧼 Clear all stored chat data
export async function clearAllChatData() {
  const db = await openDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)
  store.delete('chat')
  store.delete('thread')
  return tx.done || tx.complete
}
